import { useLocation } from 'react-router'

import { ProductSidebar, ShellRail, ShellSlots, useProductShell } from '@/components/sidebar'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { useSidebar } from '@/components/ui/sidebar'
import { SidebarTab } from '@/lib/sidebarTab'
import { cn } from '@/lib/utils'
import { useCurrentUser } from '@/models/user'
import { useActiveSidebarTab } from './sidebar/ActiveSidebarTab'
import { MobileNavDrawer } from './sidebar/MobileNavDrawer'
import { isProductOffer, SIDEBAR_TAB_ENTITY, SIDEBAR_TAB_LABEL } from './sidebar/navigation'
import { SidebarTabContent } from './sidebar/SidebarTabContent'

/** Room for the longest row label ("Expiration timeline") now the header has moved to the top bar. */
const RAIL_WIDTH = '12rem'

export function AppSidebar() {
  const { pathname } = useLocation()
  const { user } = useCurrentUser({ enabled: true })
  const { open, setOpen, isMobile, openMobile, setOpenMobile } = useSidebar()
  const { tab: activeTab, switchTab, picked } = useActiveSidebarTab()
  const shell = useProductShell()

  if (!user || !activeTab) {
    return null
  }

  const content = <SidebarTabContent tab={activeTab} user={user} pathname={pathname} onSwitch={switchTab} />

  if (isMobile && shell && activeTab !== SidebarTab.Console) {
    return (
      <ShellSlots
        menu={
          <MobileNavDrawer
            user={user}
            activeTab={activeTab}
            onSwitch={switchTab}
            onNavigate={shell.closeMenu}
          />
        }
      />
    )
  }

  if (isMobile) {
    return (
      <Sheet open={openMobile} onOpenChange={setOpenMobile}>
        <SheetContent
          side="left"
          // The console's sidebar brings its own close button; the product menu keeps the sheet's, unseen until keyboard focus.
          className={cn(
            'w-72 bg-white p-0',
            activeTab === SidebarTab.Console
              ? '[&>button]:hidden'
              : '[&>button]:opacity-0 [&>button:focus-visible]:opacity-100'
          )}
        >
          <SheetHeader className="sr-only">
            <SheetTitle>Navigation</SheetTitle>
            <SheetDescription>Every Cai product you hold, and its pages.</SheetDescription>
          </SheetHeader>
          {activeTab === SidebarTab.Console ? (
            // The console is Cai's own and keeps its sidebar; customers get the product groups.
            <ProductSidebar
              drawer
              open
              onOpenChange={setOpenMobile}
              label={SIDEBAR_TAB_LABEL[activeTab]}
              name={SIDEBAR_TAB_LABEL[SidebarTab.Console]}
              className="bg-white"
            >
              {content}
            </ProductSidebar>
          ) : (
            <MobileNavDrawer
              user={user}
              activeTab={activeTab}
              onSwitch={switchTab}
              onNavigate={() => setOpenMobile(false)}
            />
          )}
        </SheetContent>
      </Sheet>
    )
  }

  // An offer has no pages to list; the blank state in the main area makes the pitch.
  if (isProductOffer(activeTab, user, pathname, picked)) return null

  // In the shared shell the rail is the shell's, mounted once for every product; Insure lists its rows in it.
  if (shell) return <ShellRail>{content}</ShellRail>

  return (
    <ProductSidebar
      header={false}
      width={RAIL_WIDTH}
      open={open}
      onOpenChange={setOpen}
      label={SIDEBAR_TAB_LABEL[activeTab]}
      entity={SIDEBAR_TAB_ENTITY[activeTab]}
      // Pinned under the top bar rather than to the top of the window.
      className="top-(--app-top-bar-height) h-(--app-rail-height) bg-white"
    >
      {content}
    </ProductSidebar>
  )
}
